import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,  } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { toast } from 'react-toastify';
import UserModal from './modals/users/userModal';
import UserPwModal from './modals/users/userPwModal';
import DeleteUserModal from './modals/users/deleteUserModal';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import PageHeader from '../common/banner/pageHeader';
import { sortAdminItems } from '../common/utils/tableUtil';
import SearchBar from '../common/search/SearchBar';

const UsersTable = ({ items, onToggle, sortConfig, onSortChange, fetchUsers, setUsers }) => {

  const handleUpdate = (user) =>  {    
    toast.success(`${user.username} Updated`, { autoClose: 3000 });
    fetchUsers();
  };

  const handlePWUpdate = (user) =>  {    
    toast.success(`Password Updated For ${user.username} `, { autoClose: 3000 });
  };

  const handleDelete = (user) =>  {
    setUsers(items.filter(items => items.id !== user.id));
    toast.success(`${user.username} Deleted`, { autoClose: 3000 });
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['User Name', 'First Name', 'Last Name', 'Email', 'Group', 'CLI', 'Disabled', 'CCG Employee'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}
              <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.username}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.firstname}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.lastname}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.emailaddr}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.groupname}</TableCell>                
                <TableCell> <Switch checked={!!item.cli} onChange={(e) => onToggle(item, 'cli', e.target.checked)} /> </TableCell>
                <TableCell> <Switch checked={!!item.disabled} onChange={(e) => onToggle(item, 'disabled', e.target.checked)} /> </TableCell>
                <TableCell> <Switch checked={!!item.ccg_employee} onChange={(e) => onToggle(item, 'ccg_employee', e.target.checked)} /> </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1}>
                        <UserPwModal user={item} onTriggerPWUpdate={handlePWUpdate} />
                        <UserModal user={item} onTriggerUpdate={handleUpdate} />
                        <DeleteUserModal user ={ item } onTriggerDelete={handleDelete} />
                    </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default function Users() {
    const [users, setUsers] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
                
            const response = await fetch(`${process.env.REACT_APP_API}/users/`);            
            if (!response.ok) {
                toast.error(`HTTP error! status: ${response.status}`);
            } else {
                const json = await response.json();
                setUsers(json.data);
            }
        } catch (error) {
            toast.error(error);
        } 
    };

    const handleCreate = (user) =>  {
      toast.success(`${user.username} Created`, { autoClose: 3000 });
      fetchUsers();
    };


    const handleUpdate = async (user) => {
          if ( user.disabled ) {
              user.cli = false;
          }
         const res = await fetch(`${process.env.REACT_APP_API}/users/update`, {
             method: 'PUT',
             headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify(user)
         });
         if (!res.ok) {
            toast.error(`Failed to update user : ${res.status}`);
          } else {
            
            const userId = user.id;
            const updatedUser = users.find(user => user.id === userId);
            setUsers((prev) => prev.map((p) => (p.id === user.id ? user : p)));

            if (( user.disabled ) && ( updatedUser.disabled !== user.disabled )) {
              toast.success(`${user.username} Is No Longer Authorized To Use The System`);
            } else if (( !user.disabled ) && ( updatedUser.disabled !== user.disabled )) {
              toast.success(`${user.username} Is Now Authorized To Use The System`);
            } else if (( user.cli ) && ( updatedUser.cli !== user.cli )) {
              toast.success(`CLI Access Added For ${user.username}`);
            } else if (( !user.cli ) && ( updatedUser.cli !== user.cli )) {
              toast.success(`CLI Access Removed For ${user.username}`);
            } else if (( user.ccg_employee ) && ( updatedUser.ccg_employee !== user.ccg_employee )) {
              toast.success(`${user.username} Marked As CCG Employee`);
            } else if (( !user.ccg_employee ) && ( updatedUser.ccg_employee !== user.ccg_employee )) {
              toast.success(`${user.username} Marked As A Non-CCG Employee`);
            }
          }
     };

    const handleSortChange = (newConfig) => {
        setSortConfig(newConfig);
    };

    const filtered = users.filter((item) => {
        const s = searchQuery.toLowerCase();
        return ['username', 'firstname', 'lastname', 'emailaddr', 'groupname'].some((field) => item[field]?.toLowerCase().includes(s));
    });

  const sorted = sortAdminItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
        <Box sx={{pt: 2}}>
            <ClassificationBannerHeader />
            <PageHeader title="Users" />
            <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>                    
                  <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search Users..."
                      label="Search Users..."
                      width={200}
                  />                
            </Stack>
          
            <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                <UsersTable
                    items={paginated}
                    onToggle={(item, key, value) => handleUpdate({ ...item, [key]: value })}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    fetchUsers={fetchUsers}
                    setUsers={setUsers}
                />
                <TablePagination
                    component="div"
                    count={sorted.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                />
            </TableContainer>
            <UserModal user='' onTriggerCreate={handleCreate} />
        </Box>
    </Container>
  )
}
